import React, { useCallback, useMemo, useRef } from 'react'
import {
    compName_character,
    livePortsNumber,
} from '../logic/panels/helpers/LiveButton'
import renderImage from '../../source/importImg'
import InputNumber from './InputNumber'
import './MotorSlider.css'
import { useSelector, useDispatch } from 'react-redux'

/**
 * Motor / Servo / Output Slider for PlayScreen.
 * Supports dual-port motors (dc, geared, dynamex) and single-port components
 * (servo, PWM, LED, digital output). Includes a Live button that sends bytes
 * to connected hardware via the serialWorker.
 *
 * Props:
 *   mapping        – output state key (e.g. 'motor_speed')
 *   outputStates   – current output state map from PlayScreen
 *   onChange        – (mapping, value) callback
 *   rangImgName    – slider background image name
 *   title          – slider title (e.g. 'Intensity')
 *   label          – component display name
 *   min            – slider min (default -100)
 *   max            – slider max (default 100)
 *   showLiveBtn    – whether to show the Live button (default true)
 *   port           – base port from assembly (e.g. 'M1', 'A', 'servo_motor1')
 *   componentType  – hardware component type (e.g. 'dc_motor', 'servo_motor')
 */

const DUAL_PORT_MOTORS = [
    'mini_geared_motor',
    'geared_motor',
    'dc_motor',
    'dynamex_motor',
]

function MotorSlider({
    mapping,
    outputStates = {},
    onChange,
    rangImgName,
    title = 'Intensity',
    label,
    min = -100,
    max = 100,
    port,
    componentType,
    isLiveStep,
}) {
    const worker = useSelector((state) => state.worker)
    const webSerial = useSelector((state) => state.webSerial)
    const dispatch = useDispatch()
    const isLiveActive = !!webSerial?.isConnected
    const selectorRef = useRef(null)

    const val = Number(outputStates[mapping]) || 0
    const isDualPort = DUAL_PORT_MOTORS.includes(componentType)

    // Derive port1/port2 for dual-port motors
    const { port1, port2 } = useMemo(() => {
        if (!isDualPort || !port) return { port1: port, port2: null }
        const match = port.match(/^([A-Za-z]+)(\d+)$/)
        if (match) {
            const letter = match[1]
            const num = parseInt(match[2])
            return { port1: port, port2: `${letter}${num + 1}` }
        }
        // Single letter port: A → A1, A2
        return { port1: port + '1', port2: port + '2' }
    }, [port, isDualPort])

    // Send a single Live Write (LW) byte to the hardware
    const liveChange = useCallback(
        (targetPort, value) => {
            if (!worker || !webSerial?.isConnected) return
            const connectedDevice = sessionStorage.getItem('connectedDevice')
            if (!connectedDevice) return

            let adjustedValue = value
            if (componentType === 'servo_motor_270') {
                adjustedValue = Math.round(0.6667 * value)
            } else if (componentType === 'servo_motor_360') {
                adjustedValue = 100 + value
            }

            const charCode =
                compName_character[componentType] || 'P'.charCodeAt(0)
            const portNum =
                livePortsNumber?.[connectedDevice]?.[targetPort] ||
                livePortsNumber?.Ace?.[targetPort]
            if (portNum === undefined) return

            const data = [
                'L'.charCodeAt(0),
                'W'.charCodeAt(0),
                charCode,
                portNum,
                adjustedValue,
            ]
            worker.postMessage({ type: 'writeArray', value: data })
        },
        [componentType, worker, webSerial]
    )

    // On mouse-up: send live bytes for motor direction (dual-port) or direct value
    const handleMouseUp = useCallback(async () => {
        // Block local live change if global PlayScreen Live is active!
        if (!isLiveActive || !port || isLiveStep) return

        const currentVal = Number(outputStates[mapping]) || 0

        if (isDualPort) {
            if (currentVal < 0) {
                const absVal =
                    componentType === 'dynamex_motor'
                        ? Math.round(Math.abs(currentVal) * 0.8)
                        : Math.abs(currentVal)
                liveChange(port1, absVal)
                await new Promise((r) => setTimeout(r, 100))
                liveChange(port2, 0)
            } else if (currentVal > 0) {
                const v =
                    componentType === 'dynamex_motor'
                        ? Math.round(currentVal * 0.8)
                        : currentVal
                liveChange(port2, v)
                await new Promise((r) => setTimeout(r, 100))
                liveChange(port1, 0)
            } else {
                liveChange(port2, 0)
                await new Promise((r) => setTimeout(r, 100))
                liveChange(port1, 0)
            }
        } else {
            liveChange(port, currentVal)
        }
    }, [
        isLiveActive,
        port,
        isDualPort,
        outputStates,
        mapping,
        port1,
        port2,
        liveChange,
        componentType,
    ])

    const handleSliderChange = useCallback(
        (e) => {
            const v = Number(e.target.value)
            onChange(mapping, v)
            if (selectorRef.current) {
                const pct = ((v - min) / (max - min)) * 100
                selectorRef.current.style.left = pct + '%'
            }
            // Block local live change if global PlayScreen Live is active!
            if (isLiveActive && !isDualPort && port && !isLiveStep) {
                liveChange(port, v)
            }
        },
        [
            mapping,
            onChange,
            min,
            max,
            isLiveActive,
            isDualPort,
            port,
            liveChange,
            isLiveStep,
        ]
    )

    const handleInputChange = useCallback(
        (v) => {
            onChange(mapping, v)

            // Block local live change if global PlayScreen Live is active!
            if (isLiveActive && port && !isLiveStep) {
                if (isDualPort) {
                    // For motors, treat input change like mouse-up
                    if (v < 0) {
                        const absVal =
                            componentType === 'dynamex_motor'
                                ? Math.round(Math.abs(v) * 0.8)
                                : Math.abs(v)
                        liveChange(port1, absVal)
                        setTimeout(() => liveChange(port2, 0), 100)
                    } else if (v > 0) {
                        const out =
                            componentType === 'dynamex_motor'
                                ? Math.round(v * 0.8)
                                : v
                        liveChange(port2, out)
                        setTimeout(() => liveChange(port1, 0), 100)
                    } else {
                        liveChange(port2, 0)
                        setTimeout(() => liveChange(port1, 0), 100)
                    }
                } else {
                    liveChange(port, v)
                }
            }
        },
        [
            mapping,
            onChange,
            isLiveActive,
            port,
            isDualPort,
            port1,
            port2,
            liveChange,
            componentType,
        ]
    )

    const sliderPercent = ((val - min) / (max - min)) * 100

    const sliderBgStyle = rangImgName
        ? {
              backgroundImage: `url(${renderImage(rangImgName)})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% 100%',
          }
        : {}

    return (
        <div className="play-motor-slider">
            {/* Top row: label + Live button */}
            <div className="play-motor-slider__header">
                <span className="play-motor-slider__name">{label}</span>
            </div>

            {/* Slider row: title + track */}
            <div className="play-motor-slider__slider-area">
                <div className="play-motor-slider__row">
                    <p className="play-motor-slider__slider-label">{title}</p>
                    <div className="play-motor-slider__track-cell">
                        <div className="play-motor-slider__range-wrap">
                            <div
                                className="play-motor-slider__range-bg"
                                style={sliderBgStyle}
                            >
                                <input
                                    type="range"
                                    min={min}
                                    max={max}
                                    value={val}
                                    className="play-motor-range-input"
                                    onChange={handleSliderChange}
                                    onMouseUp={handleMouseUp}
                                    onTouchEnd={handleMouseUp}
                                />
                                <div
                                    ref={selectorRef}
                                    className="play-motor-selector"
                                    style={{ left: `${sliderPercent}%` }}
                                >
                                    <div className="play-motor-selector__thumb"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Min/Max labels */}
                <div className="play-motor-slider__minmax"></div>
                {/* InputNumber */}
                <div className="play-motor-slider__input-row">
                    <InputNumber
                        value={val}
                        onChange={handleInputChange}
                        min={min}
                        max={max}
                        disabled={false}
                    />
                    <p className="play-motor-slider__minmax-left">{min}</p>
                    <p className="play-motor-slider__minmax-right">{max}</p>
                </div>
            </div>
        </div>
    )
}

export default MotorSlider
